import React from "react";
import { Box, Divider, Paper, Typography } from "@mui/material";
import { reportSectionDefinitions } from "./reportDefinitions";

function DataTable0({ rows }: { rows: any[] }) {
  if (!rows?.length) {
    return <Typography color="text.secondary">No data available.</Typography>;
  }

  const columns = Object.keys(rows[0]);

  return (
    <Box sx={{ overflowX: "auto" }}>
      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
        <thead>
          <tr>
            {columns.map((c) => (
              <th
                key={c}
                style={{
                  textAlign: "left",
                  padding: "8px",
                  borderBottom: "1px solid #D8E0E8",
                  color: "#163A5F",
                }}
              >
                {c}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i}>
              {columns.map((c) => (
                <td
                  key={c}
                  style={{
                    padding: "8px",
                    borderBottom: "1px solid #EEF3F8",
                  }}
                >
                  {typeof r[c] === "object" ? JSON.stringify(r[c]) : String(r[c] ?? "")}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </Box>
  );
}

function formatCell(value: any) {
  if (value === null || value === undefined) return "";

  if (typeof value === "number") {
    return value.toLocaleString("fr-FR");
  }

  if (typeof value === "object") {
    return JSON.stringify(value);
  }

  return String(value);
}

function DataTable({
  rows,
  sectionKey,
}: {
  rows: any[];
  sectionKey: string;
}) {
  if (!rows?.length) {
    return <Typography color="text.secondary">No data available.</Typography>;
  }

  const def = reportSectionDefinitions[sectionKey];

  const columns =
    def?.columns ||
    Object.keys(rows[0]).map((key) => ({
      key,
      label: key,
      align: "left" as const,
    }));

  return (
    <Box sx={{ overflowX: "auto" }}>
      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
        <thead>
          <tr>
            {columns.map((c) => (
              <th
                key={c.key}
                style={{
                  textAlign: c.align || "left",
                  padding: "8px",
                  borderBottom: "1px solid #D8E0E8",
                  color: "#163A5F",
                  background: "#F7FAFC",
                  whiteSpace: "nowrap",
                }}
              >
                {c.label}
              </th>
            ))}
          </tr>
        </thead>

        <tbody>
          {rows.map((r, i) => (
            <tr key={i}>
              {columns.map((c) => (
                <td
                  key={c.key}
                  style={{
                    textAlign: c.align || "left",
                    padding: "8px",
                    borderBottom: "1px solid #EEF3F8",
                    verticalAlign: "top",
                  }}
                >
                  {formatCell(r[c.key])}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </Box>
  );
}

export default function ReportPreview({ report }: { report: any }) {
  const cover = report?.cover || {};
  const summary = report?.executiveSummary || {};
  const sections = report?.sections || {};
  const meta = report?.meta || {};

  const summaryEntries = Object.entries(summary).filter(([key]) =>
    key.toLowerCase().includes("formatted") ||
    key.toLowerCase().includes("count") ||
    key.toLowerCase().includes("score") ||
    key.toLowerCase().includes("pct")
  );

  return (
    <Paper
        sx={{
          p: 4,
          borderRadius: 3,
          "@media print": {
            boxShadow: "none",
            borderRadius: 0,
            p: 2,
          },
        }}
      >
      <Typography sx={{ fontSize: 26, fontWeight: 800, color: "#163A5F" }}>
        {cover.title || report?.meta?.reportName}
      </Typography>

      <Typography sx={{ mt: 0.5, color: "#66788A" }}>
        {cover.subtitle}
      </Typography>

      <Typography sx={{ mt: 1, fontSize: 12, color: "#66788A" }}>
        Generated: {cover.generatedAt}
      </Typography>

      <Box
        sx={{
          mt: 2,
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "repeat(4, 1fr)" },
          gap: 1,
        }}
      >
        {[
          ["Report", meta.reportCode],
          ["Period", meta.periodCode],
          ["Cycle", meta.cycleCode],
          ["Scenario", meta.scenarioCode],
          ["Cost Center", meta.costCenterCode || "All"],
          ["Location", meta.locationCode || "All"],
          ["As Of", meta.asOfDate],
          ["Language", meta.lang],
        ].map(([label, value]) => (
          <Box key={label} sx={{ p: 1, bgcolor: "#F7FAFC", borderRadius: 1 }}>
            <Typography sx={{ fontSize: 11, color: "#66788A" }}>{label}</Typography>
            <Typography sx={{ fontSize: 13, fontWeight: 700 }}>{value}</Typography>
          </Box>
        ))}
      </Box>

      <Divider sx={{ my: 3 }} />

      <Typography sx={{ fontSize: 18, fontWeight: 800, mb: 1 }}>
        Executive Summary
      </Typography>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "repeat(3, 1fr)" },
          gap: 1.5,
          mb: 3,
        }}
      >
        {Object.entries(summary).map(([key, value]) => (
          <Box
            key={key}
            sx={{
              p: 1.5,
              border: "1px solid #E2E8EE",
              borderRadius: 2,
              bgcolor: "#FAFCFE",
            }}
          >
            <Typography sx={{ fontSize: 12, color: "#66788A" }}>
              {key}
            </Typography>
            <Typography sx={{ fontWeight: 800, color: "#163A5F" }}>
              {typeof value === "object" ? JSON.stringify(value) : String(value ?? "")}
            </Typography>
          </Box>
        ))}
      </Box>

      {Object.entries(sections).map(([key, section]: any) => {
        const def = reportSectionDefinitions[key];
        return (
          <Box key={key}
            sx={{
              mt: 3,
              breakInside: "avoid",
              pageBreakInside: "avoid",
            }}
          >
          {/* <Box key={key} sx={{ mt: 3 }}> */}
            <Typography sx={{ fontSize: 17, fontWeight: 800, color: "#163A5F" }}>
              {def?.title || section.title || key}
            </Typography>

            {Array.isArray(section.data) ? (
              <DataTable rows={section.data} sectionKey={key} />
            ) : (
              <pre style={{ whiteSpace: "pre-wrap" }}>
                {JSON.stringify(section, null, 2)}
              </pre>
            )}
          </Box>)
        }
      )}

      {/* {Object.entries(sections).map(([key, section]: any) => (
        <Box key={key} sx={{ mt: 3 }}>
          <Typography sx={{ fontSize: 17, fontWeight: 800, color: "#163A5F" }}>
            {section.title || key}
          </Typography>

          <Box sx={{ mt: 1 }}>
            {Array.isArray(section.data) ? (
              <DataTable rows={section.data} />
            ) : (
              <pre style={{ whiteSpace: "pre-wrap" }}>
                {JSON.stringify(section, null, 2)}
              </pre>
            )}
          </Box>
        </Box>
      ))} */}
    </Paper>
  );
}